import { useRef, useEffect, useCallback } from 'react'

export default function HandwriteBoard() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const drawing = useRef(false)
  const lastPos = useRef<{ x: number; y: number } | null>(null)

  const getPos = useCallback(
    (e: MouseEvent | Touch): { x: number; y: number } => {
      const c = canvasRef.current
      const rect = c?.getBoundingClientRect ? c.getBoundingClientRect() : null
      if (!c || !rect) return { x: 0, y: 0 }
      return {
        x: (e.clientX - rect.left) * (c.width / rect.width),
        y: (e.clientY - rect.top) * (c.height / rect.height),
      }
    },
    []
  )

  const startDraw = useCallback(
    (pos: { x: number; y: number }) => {
      drawing.current = true
      lastPos.current = pos
    },
    []
  )

  const moveDraw = useCallback(
    (pos: { x: number; y: number }) => {
      if (!drawing.current || !lastPos.current) return
      const ctx = canvasRef.current?.getContext('2d')
      if (!ctx) return

      ctx.beginPath()
      ctx.moveTo(lastPos.current.x, lastPos.current.y)
      ctx.lineTo(pos.x, pos.y)
      ctx.strokeStyle = '#e2e8f0'
      ctx.lineWidth = 3
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      ctx.stroke()

      lastPos.current = pos
    },
    []
  )

  const endDraw = useCallback(() => {
    drawing.current = false
    lastPos.current = null
  }, [])

  const clear = useCallback(() => {
    const c = canvasRef.current
    if (!c) return
    const ctx = c.getContext('2d')
    if (!ctx) return
    ctx.clearRect(0, 0, c.width, c.height)
  }, [])

  // 初始化 Canvas 尺寸（2x 高清）
  useEffect(() => {
    const c = canvasRef.current
    if (!c) return
    const dpr = window.devicePixelRatio || 1
    const parent = c.parentElement
    const rect = parent?.getBoundingClientRect ? parent.getBoundingClientRect() : null
    if (!rect) return
    c.width = rect.width * dpr
    c.height = rect.height * dpr
    c.style.width = rect.width + 'px'
    c.style.height = rect.height + 'px'
    const ctx = c.getContext('2d')!
    ctx.scale(dpr, dpr)
  }, [])

  // 绑定事件（每个 handler 入口先判空，防御卸载竞态）
  useEffect(() => {
    const c = canvasRef.current
    if (!c) return

    const guard = () => canvasRef.current

    const onMouseDown = (e: MouseEvent) => {
      if (!guard()) return
      startDraw(getPos(e))
    }
    const onMouseMove = (e: MouseEvent) => {
      if (!guard()) return
      moveDraw(getPos(e))
    }
    const onMouseUp = () => {
      if (!guard()) return
      endDraw()
    }
    const onTouchStart = (e: TouchEvent) => {
      if (!guard()) return
      e.preventDefault()
      const touch = e.touches[0]
      if (!touch) return
      startDraw(getPos(touch))
    }
    const onTouchMove = (e: TouchEvent) => {
      if (!guard()) return
      e.preventDefault()
      const touch = e.touches[0]
      if (!touch) return
      moveDraw(getPos(touch))
    }
    const onTouchEnd = (e: TouchEvent) => {
      if (!guard()) return
      e.preventDefault()
      endDraw()
    }

    c.addEventListener('mousedown', onMouseDown)
    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
    c.addEventListener('touchstart', onTouchStart, { passive: false })
    c.addEventListener('touchmove', onTouchMove, { passive: false })
    c.addEventListener('touchend', onTouchEnd, { passive: false })

    return () => {
      c.removeEventListener('mousedown', onMouseDown)
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
      c.removeEventListener('touchstart', onTouchStart)
      c.removeEventListener('touchmove', onTouchMove)
      c.removeEventListener('touchend', onTouchEnd)
    }
  }, [startDraw, moveDraw, endDraw, getPos])

  return (
    <div className="w-full flex flex-col gap-2">
      <div className="relative w-full aspect-[4/3] rounded-xl overflow-hidden border border-white/5 bg-zinc-900/50">
        <canvas
          ref={canvasRef}
          className="absolute inset-0 w-full h-full"
          style={{ touchAction: 'none' }}
        />
      </div>
      <button
        onClick={clear}
        className="self-end px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-zinc-400 hover:text-white text-xs transition-all duration-150"
      >
        ✕ 清除
      </button>
    </div>
  )
}
